import React, { useEffect, useState, Fragment } from 'react';
import { ExperienceRecord } from '../types/experience';
import { TierBadge } from './ui/Badge';
import { CheckCircleIcon } from 'lucide-react';
interface GuarantorReviewProps {
  data: ExperienceRecord[];
  pickedGuarantorId: string | null;
  onPickGuarantor: (id: string | null) => void;
}
export function GuarantorReview({
  data,
  pickedGuarantorId,
  onPickGuarantor
}: GuarantorReviewProps) {
  const [saved, setSaved] = useState(false);
  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };
  // reset saved state when selection changes
  useEffect(() => {
    setSaved(false);
  }, [pickedGuarantorId]);
  const formatCurrency = (val: number | null) => {
    if (val === null || val === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };
  const formatNumber = (val: number | null, decimals = 0) => {
    if (val === null || val === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: decimals
    }).format(val);
  };
  // Group fields by category for cleaner rendering
  const sections = [{
    title: 'Transaction Summary',
    fields: [{
      label: 'Cumulative Sale Price',
      key: 'Transactions_Cumulative_Sale_Price__c',
      format: formatCurrency
    }, {
      label: 'Avg Sales Price',
      key: 'Transactions_Average_Sales_Price__c',
      format: formatCurrency
    }, {
      label: 'Avg Project Duration (Mo)',
      key: 'Transactions_Average_Project_Duration__c',
      format: (v: number | null) => formatNumber(v, 1)
    }]
  }, {
    title: 'Fix & Flip',
    fields: [{
      label: 'Count',
      key: 'Number_of_Flips__c',
      format: (v: number | null) => formatNumber(v)
    }, {
      label: 'Cumulative Sale Price',
      key: 'Fix_Flip_Cumulative_Sale_Price__c',
      format: formatCurrency
    }, {
      label: 'Avg Sale Price',
      key: 'Fix_Flip_Average_Sale_Price__c',
      format: formatCurrency
    }, {
      label: 'Avg Duration (Mo)',
      key: 'Fix_Flip_Average_Project_Duration__c',
      format: (v: number | null) => formatNumber(v, 1)
    }]
  }, {
    title: 'New Construction',
    fields: [{
      label: 'Count',
      key: 'Number_of_New_Builds__c',
      format: (v: number | null) => formatNumber(v)
    }, {
      label: 'Cumulative Sale Price',
      key: 'New_Construction_Cumulative_Sale_Price__c',
      format: formatCurrency
    }, {
      label: 'Avg Sale Price',
      key: 'New_Construction_Average_Sale_Price__c',
      format: formatCurrency
    }, {
      label: 'Avg Duration (Mo)',
      key: 'New_Construction_Avg_Project_Duration__c',
      format: (v: number | null) => formatNumber(v, 1)
    }]
  }, {
    title: 'GC Projects',
    fields: [{
      label: 'Count',
      key: 'Number_of_GC_Projects__c',
      format: (v: number | null) => formatNumber(v)
    }]
  }, {
    title: 'Budgets',
    fields: [{
      label: 'Avg Rehab Budget',
      key: 'Average_Rehab_Budget__c',
      format: formatCurrency
    }, {
      label: 'Max Rehab Budget',
      key: 'Max_Rehab_Budget__c',
      format: formatCurrency
    }, {
      label: 'Avg GC Budget',
      key: 'Average_GC_Budget__c',
      format: formatCurrency
    }, {
      label: 'Max GC Budget',
      key: 'Max_GC_Budget__c',
      format: formatCurrency
    }, {
      label: 'Avg Construction Budget',
      key: 'Average_Construction_Budget__c',
      format: formatCurrency
    }, {
      label: 'Max Construction Budget',
      key: 'Max_Construction_Budget__c',
      format: formatCurrency
    }]
  }];
  return <div className="flex-1 overflow-auto bg-white flex flex-col" data-id="element-26">
      <table className="min-w-full border-collapse text-sm flex-1" data-id="element-27">
        <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm" data-id="element-28">
          <tr data-id="element-29">
            <th className="sticky left-0 z-30 bg-gray-50 border-b border-r border-gray-200 px-6 py-4 text-left font-semibold text-gray-500 w-72" data-id="element-30">
              Metric
            </th>
            {data.map(guarantor => {
            const isPicked = pickedGuarantorId === guarantor.Id;
            return <th key={guarantor.Id} className={`border-b border-r px-6 py-4 text-left min-w-[260px] transition-colors ${isPicked ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}`} data-id="element-31">
                  <div className="flex items-start justify-between gap-3" data-id="element-32">
                    <div className="min-w-0" data-id="element-33">
                      <div className="font-bold text-gray-900 text-base mb-0.5" data-id="element-34">
                        {guarantor.Guarantor__c}
                      </div>
                      <div className="text-xs text-gray-500 truncate mb-2" data-id="element-35">
                        {guarantor.Opportunity__c}
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-36">
                        <TierBadge tier={guarantor.Experience_Tier__c} data-id="element-37" />
                        <span className="text-xs text-gray-500" data-id="element-38">
                          {guarantor.Total_Experience__c} Exp
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 mt-0.5" data-id="element-39">
                      {isPicked ? <button onClick={() => onPickGuarantor(null)} className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors" data-id="element-40">
                          <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-41" />
                          <span data-id="element-42">Selected</span>
                        </button> : <button onClick={() => onPickGuarantor(guarantor.Id)} className="px-3 py-1.5 rounded-full border border-gray-300 text-gray-500 text-xs font-semibold hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" data-id="element-43">
                          Use
                        </button>}
                    </div>
                  </div>
                </th>;
          })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100" data-id="element-44">
          {sections.map(section => <Fragment key={section.title} data-id="element-45">
              <tr className="bg-gray-100/60" data-id="element-46">
                <td colSpan={data.length + 1} className="sticky left-0 z-10 bg-gray-100/60 border-b border-gray-200 py-2.5 px-6 font-bold text-gray-500 uppercase tracking-wider text-xs" data-id="element-47">
                  {section.title}
                </td>
              </tr>
              {section.fields.map(field => <tr key={field.key} className="even:bg-gray-50/40 hover:bg-blue-50/40 transition-colors" data-id="element-48">
                  <td className="sticky left-0 z-10 bg-inherit border-r border-gray-200 py-3.5 px-6 text-sm font-medium text-gray-600 truncate" data-id="element-49">
                    {field.label}
                  </td>
                  {data.map(guarantor => {
              const value = guarantor[field.key as keyof ExperienceRecord];
              const formatted = field.format(value as any);
              const isEmpty = formatted === '—';
              const isPicked = pickedGuarantorId === guarantor.Id;
              return <td key={`${guarantor.Id}-${field.key}`} className={`border-r py-3.5 px-6 text-right transition-colors ${isPicked ? 'bg-blue-50/40 border-blue-100' : 'border-gray-100'}`} data-id="element-50">
                        <span className={`text-sm tabular-nums ${isEmpty ? 'text-gray-300' : isPicked ? 'text-blue-900 font-semibold' : 'text-gray-900 font-medium'}`} data-id="element-51">
                          {formatted}
                        </span>
                      </td>;
            })}
                </tr>)}
            </Fragment>)}
        </tbody>
      </table>
    </div>;
}